import { useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import constate from 'constate'
import queryString from 'query-string'
import { OrderDirection, CoverLetterOrderField } from 'generated/graphql'
import { MOBILE_BUTTON_SIZE, PC_BUTTON_SIZE } from 'constants/paginations'
import usePagination from 'hooks/usePagination'
import { parseValues, filterEmptyField } from 'utils/utils'

export enum TabState {
  ALL = 'all',
  MAJOR_COMPANY = 'major_company',
  PUBLIC_INSTITUTIONS = 'public_institutions',
  BANKS = 'banks',
  INTERN = 'intern',
}

export interface SearchState {
  tab?: TabState
  organizationName?: string
  role?: string
  type?: string
  keyword?: string
  sort?: CoverLetterOrderField
  direction?: OrderDirection
  page?: number
}

export interface UseSearchStateProps {
  searchState: SearchState
  paginationMaxButtons?: number
  onSearchStateChange?: (newState: SearchState) => void
}

/**
 * 자소서 검색 쿼리 파라미터 필터링 함수
 * - 빈 값(null, undefined, 빈 문자열) 제거
 * - 기본값 제거: page=1, tab='all'
 */
export function filterCoverLetterSearchParams(params: Record<string, unknown>) {
  const filtered = filterEmptyField(params)

  // 기본값 제거
  if (filtered.page === 1) {
    delete filtered.page
  }
  if (filtered.tab === TabState.ALL) {
    delete filtered.tab
  }

  return filtered
}

function initializeState(newState: SearchState, updatedState: SearchState) {
  if (!Object.keys(updatedState).includes('page')) {
    newState.page = 1
  }

  if (Object.keys(updatedState).includes('tab')) {
    newState.organizationName = ''
    newState.role = ''
    newState.keyword = ''
    newState.page = 1
  }

  return newState
}

function useSearchState(props?: UseSearchStateProps) {
  const { searchState, onSearchStateChange, paginationMaxButtons } = props || {}
  const { organizationName, role, keyword } = searchState || {}
  const isAfterSearch = !!organizationName || !!role || !!keyword
  const defaultOrderBy = isAfterSearch
    ? CoverLetterOrderField.RELEVANCE
    : CoverLetterOrderField.RECENT_SCRAP_COUNT

  function handleSearchStateChange(updatedState: SearchState) {
    let newState = {
      ...searchState,
      ...queryString.parse(window.location.search),
      ...updatedState,
    }

    newState = initializeState(newState, updatedState)
    newState = parseValues(newState, { parseNumbers: true })

    if (onSearchStateChange) {
      onSearchStateChange(newState)
    }
  }

  function setTabState(newTabState: TabState) {
    handleSearchStateChange({ tab: newTabState })
  }

  function setOrderBy(orderField: CoverLetterOrderField) {
    handleSearchStateChange({ sort: orderField })
  }

  function setActivePage(newActivePage: number) {
    handleSearchStateChange({ page: newActivePage })
  }

  const theme = useTheme()
  const isXSDown = useMediaQuery(theme.breakpoints.down('xs'))

  const pagination = usePagination({
    activePage: searchState?.page || 1,
    setActivePage,
    maxButtons:
      paginationMaxButtons || (isXSDown ? MOBILE_BUTTON_SIZE : PC_BUTTON_SIZE),
  })

  return {
    isAfterSearch,
    tabState: searchState?.tab || TabState.ALL,
    orderBy: {
      orderField: searchState?.sort || defaultOrderBy,
      orderDirection: searchState?.direction || OrderDirection.DESC,
    },
    search: { organizationName, role, keyword },
    page: searchState?.page || 1,
    pagination,
    setTabState,
    setOrderBy,
  }
}

export const [SearchStateContextProvider, useSearchStateContext] =
  constate(useSearchState)
